import React from "react";
import PageHeader from "@/components/PageHeader";
import { useCookieConsent } from "@/context/CookieContext";

export default function CookiePolicy() {
  const { openPreferences } = useCookieConsent();
  const lastUpdated = "1 February 2026";

  return (
    <>
      <PageHeader
        testId="cookie-policy-header"
        eyebrow="Legal"
        title="Cookie Policy"
        intro="How Evergreen uses cookies and similar technologies, and how you can manage your preferences at any time."
      />

      <section className="evg-section bg-white">
        <div className="evg-container max-w-4xl">
          <span
            className="text-xs uppercase tracking-[0.18em] text-[#0A1F44]/55"
            data-testid="cookie-policy-last-updated"
          >
            Last Updated: {lastUpdated}
          </span>

          <div className="mt-10 space-y-10">
            <Block title="1. What Are Cookies?">
              Cookies are small text files placed on your device when you visit a website. They are
              widely used to make sites work, improve performance, understand visitor behaviour and
              support marketing activities.
            </Block>

            <Block title="2. Categories of Cookies We Use">
              We classify cookies into three categories:
              <ul className="mt-5 space-y-3 list-none">
                <li className="pl-6 border-l-2 border-[#D4AF37]">
                  <strong>Strictly Necessary</strong> — required for site security, form submission,
                  session management and consent storage. Always enabled.
                </li>
                <li className="pl-6 border-l-2 border-[#D4AF37]">
                  <strong>Analytics</strong> — used to understand visitor behaviour (e.g. Google
                  Analytics, Plausible, Microsoft Clarity). Disabled until consent is granted.
                </li>
                <li className="pl-6 border-l-2 border-[#D4AF37]">
                  <strong>Marketing</strong> — used for campaign tracking and audience building
                  (e.g. LinkedIn Insight Tag). Disabled by default.
                </li>
              </ul>
            </Block>

            <Block title="3. Duration">
              Cookies may be session-based (deleted when you close your browser) or persistent
              (remaining on your device for a defined period). Specific durations vary by provider.
            </Block>

            <Block title="4. Third-Party Services">
              Where we engage third-party services for analytics or marketing, those providers may
              process limited personal information in line with their own privacy notices.
            </Block>

            <Block title="5. Managing Your Preferences">
              You can update your cookie preferences at any time using the Cookie Settings link in
              the footer or via your browser settings.
            </Block>

            <Block title="6. Updates">
              We may update this policy as our cookie usage evolves. Material changes will be
              reflected by an updated “Last Updated” date.
            </Block>
          </div>

          <div className="mt-12 pt-8 border-t border-[#0A1F44]/10">
            <button
              type="button"
              onClick={openPreferences}
              data-testid="cookie-policy-open-preferences"
              className="text-[11px] uppercase tracking-[0.2em] font-medium px-5 py-3 border border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white transition-colors"
            >
              Manage Cookie Preferences
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div className="pb-8 border-b border-[#0A1F44]/10 last:border-0">
      <h2 className="font-serif text-2xl md:text-3xl leading-tight">{title}</h2>
      <div className="mt-5 evg-body">{children}</div>
    </div>
  );
}
